import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faRobot, faTrophy, faFire, faHeartPulse, faStopwatch, faMedal } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { SPORT_LABEL } from '../utils/sportData';
import { formatDuration } from '../utils/dateUtils';
import { formatPoints } from '../utils/scoring';
import '../styles/WeeklyBestCard.css';

interface ZoneBreakdown {
  zone: number;
  time_sec: number;
  points: number;
}

interface WeeklyBestActivity {
  id: number;
  strava_activity_id: number;
  sport_type: string;
  activity_name: string | null;
  started_at_utc: string;
  duration_seconds: number;
  avg_heart_rate: number | null;
  max_heart_rate: number | null;
  distance_meters: number | null;
  elevation_gain_m: number | null;
  calories: number | null;
  is_manual_entry: boolean;
  total_points: number;
  base_points: number;
  challenge_bonus_points: number;
  pr_bonus_points: number;
  zone_breakdown: ZoneBreakdown[];
}

interface WeeklyBestPlayer {
  id: number;
  display_name: string;
  avatar_url: string | null;
}

interface WeeklyBestData {
  id: number;
  activity: WeeklyBestActivity;
  player: WeeklyBestPlayer;
  won_duel: boolean;
  matchup_id: number | null;
  week_label: string;
}

interface Props {
  onDismiss: () => void;
}

const ZONE_NAMES = ['Rest', 'Recovery', 'Endurance', 'Tempo', 'Threshold', 'Anaerobic'];
const ZONE_COLORS = ['#9e9e9e', '#4caf77', '#2196f3', '#ffb63c', '#ff9800', '#f44336'];

function getFullLabel(sport: string): string {
  return SPORT_LABEL[sport] ?? sport;
}

/**
 * Builds a prose text (~8 sentences) with alternating elements.
 * Shows the strongest activity of the last week for all players.
 */
function buildWeeklyBestText(data: WeeklyBestData): React.ReactNode {
  const { activity, player, won_duel, week_label } = data;
  const name = player.display_name;
  const sportLabel = getFullLabel(activity.sport_type);
  const dur = formatDuration(activity.duration_seconds);
  const pts = Math.round(activity.total_points);
  const actName = activity.activity_name || sportLabel;

  // Zone breakdown
  const zones = activity.zone_breakdown || [];
  const activeZones = zones.filter(z => z.time_sec > 0).sort((a, b) => b.time_sec - a.time_sec);
  const topZone = activeZones[0];
  const topZoneName = topZone ? ZONE_NAMES[topZone.zone] : null;
  const topZoneMin = topZone ? Math.round(topZone.time_sec / 60) : 0;

  // HR info
  const hr = activity.avg_heart_rate ? Math.round(activity.avg_heart_rate) : null;

  // Challenge bonus
  const challengePts = activity.challenge_bonus_points || 0;

  // Distance
  const distM = activity.distance_meters;
  const distKm = distM ? (distM / 1000).toFixed(1) : null;

  // Elevation
  const elevM = activity.elevation_gain_m;

  // Calories
  const cal = activity.calories ? Math.round(activity.calories) : null;

  // PR/Personal Best
  const hasPR = (activity.pr_bonus_points || 0) > 0;

  // Pace/Speed
  const durMin = activity.duration_seconds / 60;
  const pace = distM && durMin > 0 ? (durMin / (distM / 1000)).toFixed(0) : null;
  const speed = distM && durMin > 0 ? ((distM / 1000) / (durMin / 60)).toFixed(1) : null;

  // Build sentences with alternating elements
  const sentences: React.ReactNode[] = [];

  // 1. Opening — who did what
  sentences.push(
    <span key="s1">
      <span className="wb-player">{name}</span> absolutely crushed it last week with a{' '}
      <span className="wb-sport">{sportLabel.toLowerCase()}</span> session that earned{' '}
      <span className="wb-points">{pts} points</span> — the highest single activity across all players.
    </span>
  );

  // 2. Activity name + duration
  sentences.push(
    <span key="s2">
      The workout, called <strong>"{actName}"</strong>, lasted <strong>{dur}</strong> and was a true test of endurance and grit.
    </span>
  );

  // 3. Distance + Elevation (if available)
  if (distKm || elevM) {
    const parts: string[] = [];
    if (distKm) parts.push(`${distKm} km`);
    if (elevM) parts.push(`${Math.round(elevM)}m elevation gain`);
    sentences.push(
      <span key="s3">
        Covering <span className="wb-highlight">{parts.join(' with ')}</span>, this was a serious effort.
      </span>
    );
  }

  // 4. HR average
  if (hr) {
    sentences.push(
      <span key="s4">
        With an average heart rate of <span className="wb-highlight">{hr} bpm</span>, {name} pushed hard and stayed in the zone for the entire duration.
      </span>
    );
  }

  // 5. Pace/Speed (if available)
  if (pace) {
    sentences.push(
      <span key="s5">
        The pace was <span className="wb-highlight">{pace} min/km</span> — a solid rhythm throughout the session.
      </span>
    );
  } else if (speed) {
    sentences.push(
      <span key="s5">
        The average speed was <span className="wb-highlight">{speed} km/h</span> — a solid rhythm throughout the session.
      </span>
    );
  }

  // 6. Calories (if available)
  if (cal) {
    sentences.push(
      <span key="s6">
        Burning <span className="wb-highlight">{cal.toLocaleString()} kcal</span>, this was a real calorie crusher.
      </span>
    );
  }

  // 7. Challenge bonus
  if (challengePts > 0) {
    sentences.push(
      <span key="s7">
        <FontAwesomeIcon icon={faMedal} className="wb-icon" /> A daily challenge bonus of <span className="wb-highlight">+{Math.round(challengePts)}P</span> was also earned, adding extra fuel to the total.
      </span>
    );
  }

  // 8. PR/Personal Best
  if (hasPR) {
    sentences.push(
      <span key="s8">
        <FontAwesomeIcon icon={faTrophy} className="wb-icon" /> This was a <span className="wb-highlight">Personal Best</span> — a new record for {name}!
      </span>
    );
  }

  // 9. Duel result
  if (won_duel) {
    sentences.push(
      <span key="s9">
        This powerhouse session helped <span className="wb-player">{name}</span> secure a <span className="wb-win">victory</span> in the weekly duel — a well-deserved win.
      </span>
    );
  } else {
    sentences.push(
      <span key="s9">
        Even though this was the strongest activity of last week, <span className="wb-player">{name}</span> didn't manage to win the duel — but the effort was undeniable.
      </span>
    );
  }

  // 10. Zone breakdown summary
  if (activeZones.length > 1) {
    const zoneSummary = activeZones.slice(0, 3).map(z =>
      `${ZONE_NAMES[z.zone]} (${Math.round(z.time_sec / 60)}min)`
    ).join(', ');
    sentences.push(
      <span key="s10">
        The zone breakdown tells the story: {zoneSummary} — a balanced mix of recovery and high-intensity work.
      </span>
    );
  }

  // 11. Closing
  sentences.push(
    <span key="s11">
      <FontAwesomeIcon icon={faTrophy} className="wb-icon" /> Congratulations, <span className="wb-player">{name}</span> — you're the <span className="wb-highlight">Star of the Week</span>!
    </span>
  );

  return (
    <p className="weekly-best-text">
      {sentences.map((s, i) => (
        <React.Fragment key={i}>
          {s}
          {i < sentences.length - 1 ? ' ' : ''}
        </React.Fragment>
      ))}
    </p>
  );
}

export default function WeeklyBestCard({ onDismiss }: Props) {
  const { token } = useAuth();
  const [data, setData] = useState<WeeklyBestData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    api.get('/player/me/weekly-best', token).then((res) => {
      setData(res?.weekly_best ?? null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  const handleDismiss = () => {
    if (token) api.post('/player/me/weekly-best/dismiss', {}, token);
    onDismiss();
  };

  if (loading) {
    return (
      <div className="weekly-best-card weekly-best-card--loading">
        <div className="weekly-best-loading">Finding the strongest activity of last week…</div>
      </div>
    );
  }

  if (!data) return null;

  const { activity, player } = data;

  return (
    <div className="weekly-best-card">
      <div className="weekly-best-header">
        <div className="weekly-best-avatar-wrap">
          {player.avatar_url && !player.avatar_url.includes('robot')
            ? <img src={player.avatar_url} alt={player.display_name} className="weekly-best-avatar" title={player.display_name} />
            : <FontAwesomeIcon icon={faRobot} className="weekly-best-avatar-icon" />
          }
        </div>
        <div className="weekly-best-title">
          <span className="weekly-best-label">
            <FontAwesomeIcon icon={faFire} style={{ marginRight: 4 }} />
            Strongest Activity of last Week
          </span>
          <span className="weekly-best-name">{player.display_name}</span>
        </div>
        <button className="weekly-best-close" onClick={handleDismiss}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      {buildWeeklyBestText(data)}
    </div>
  );
}
